import { postWebAnalyticsEvents } from "~/api/web-analytics";
import type { WebAnalyticsEvent } from "~/types/analytics";
import { devError } from "./dev-log";

/**
 * Event buffer for the first-party web analytics pipeline.
 *
 * Separate from the algo pipeline's AnalyticsEventBuffer on purpose: that one posts to the
 * authenticated /api/pubsub_algo/events (CSRF + session), whereas /api/pubsub_events/web is public
 * (csrf_exempt, session:"none"). Keeping them apart lets this buffer always use the reliable
 * sendBeacon on page exit — sendBeacon cannot carry CSRF/session headers, so it is safe only for a
 * public endpoint — and means changes here can never affect the algo pipeline.
 *
 * Deliberately minimal. It only batches (to cut request count) and flushes on a timer / size / page
 * exit. It does NOT dedup or retry, unlike the algo buffer:
 *   - no retry: /pubsub_events/web creates records (publishes to Pub/Sub), and re-sending after a
 *     timeout can double-insert — the repo's retry policy forbids retrying create endpoints on
 *     timeout. Analytics is best-effort, so a failed batch is dropped rather than re-sent.
 *   - no dedup: hashing the open event body is both unreliable (nested keys) and unnecessary here;
 *     page_view fires once per navigation and each click is distinct.
 */

interface BufferConfig {
  maxBufferSize: number;
  flushIntervalMs: number;
}

const BEACON_PATH = "/api/pubsub_events/web";

class WebAnalyticsBuffer {
  private static instance: WebAnalyticsBuffer | null = null;

  private buffer: WebAnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setTimeout> | null = null;
  private readonly config: BufferConfig;

  // Stable handler references so the listeners can be removed in cleanup() (no leaks across resets).
  private readonly onVisibilityChange = () => {
    if (typeof document !== "undefined" && document.hidden) {
      this.flushWithBeacon();
    }
  };
  private readonly onPageHide = () => {
    this.flushWithBeacon();
  };

  private constructor(config?: Partial<BufferConfig>) {
    this.config = {
      maxBufferSize: config?.maxBufferSize ?? 10,
      flushIntervalMs: config?.flushIntervalMs ?? 5000,
    };
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", this.onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      // pagehide is the reliable unload signal; we intentionally skip beforeunload (redundant here and
      // it can disqualify the page from the browser's back/forward cache).
      window.addEventListener("pagehide", this.onPageHide);
    }
  }

  static getInstance(config?: Partial<BufferConfig>): WebAnalyticsBuffer {
    if (!WebAnalyticsBuffer.instance) {
      WebAnalyticsBuffer.instance = new WebAnalyticsBuffer(config);
    }
    return WebAnalyticsBuffer.instance;
  }

  static resetInstance(): void {
    if (WebAnalyticsBuffer.instance) {
      WebAnalyticsBuffer.instance.cleanup();
      WebAnalyticsBuffer.instance = null;
    }
  }

  addEvent(event: WebAnalyticsEvent): void {
    this.buffer.push(event);
    if (this.buffer.length >= this.config.maxBufferSize) {
      void this.flush();
      return;
    }
    this.resetFlushTimer();
  }

  /** Send the buffered events once. Best-effort: on failure the batch is dropped (never re-sent). */
  async flush(): Promise<void> {
    this.clearFlushTimer();
    if (this.buffer.length === 0) return;

    const events = this.buffer;
    this.buffer = [];
    try {
      await postWebAnalyticsEvents(events);
    } catch (error) {
      devError("❌ Failed to send web events (dropped):", error);
    }
  }

  /** Flush on page backgrounding via sendBeacon (reliable when the page is being torn down). */
  private flushWithBeacon(): void {
    if (this.buffer.length === 0) return;
    const events = [...this.buffer];

    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      try {
        // text/plain is a CORS-safelisted content type, so the beacon is a "simple" request needing no
        // preflight. sendBeacon cannot perform a preflight, so an application/json beacon to the
        // cross-origin API (VITE_API_BASE_URL, e.g. api-cors.blidz.com) would be blocked and the
        // page-exit events lost. The backend parses the body as JSON regardless of Content-Type.
        const blob = new Blob([JSON.stringify({ events })], { type: "text/plain;charset=UTF-8" });
        // Trim a trailing slash on the base so VITE_API_BASE_URL="https://host/" doesn't yield a
        // double-slash path the ingress 404s.
        const baseUrl = (import.meta.env.VITE_API_BASE_URL || window.location.origin).replace(/\/+$/, "");
        if (navigator.sendBeacon(`${baseUrl}${BEACON_PATH}`, blob)) {
          this.buffer = []; // clear only after the beacon is accepted, so a rejected beacon isn't lost
          return;
        }
      } catch (error) {
        devError("❌ web sendBeacon failed:", error);
      }
    }
    // Beacon unavailable / rejected: fall back to a normal fetch flush. Best-effort only — like any
    // non-keepalive fetch it may be aborted if the page is already unloading, in which case the batch
    // is lost (sendBeacon is the reliable path; this fallback mainly helps the tab-still-alive case).
    void this.flush();
  }

  private resetFlushTimer(): void {
    this.clearFlushTimer();
    this.flushTimer = setTimeout(() => this.flush(), this.config.flushIntervalMs);
  }

  private clearFlushTimer(): void {
    if (this.flushTimer) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }

  private cleanup(): void {
    this.clearFlushTimer();
    if (typeof document !== "undefined") {
      document.removeEventListener("visibilitychange", this.onVisibilityChange);
    }
    if (typeof window !== "undefined") {
      window.removeEventListener("pagehide", this.onPageHide);
    }
    if (this.buffer.length > 0) this.flushWithBeacon();
  }
}

export const getWebAnalyticsBuffer = (config?: Partial<BufferConfig>) => WebAnalyticsBuffer.getInstance(config);
export const resetWebAnalyticsBuffer = () => WebAnalyticsBuffer.resetInstance();
